import { useAtomValue } from 'jotai';

import { createNewChatSession } from '@/entities/ask-syncly/api';
import { AskMode, QuestionAnswerBlockStatus } from '@/entities/ask-syncly/ask-syncly-constants';
import type { DateRangeState } from '@/shared/components/shadcn/date-range-picker/date-range-picker';
import { Stage } from '@/shared/components/stage/stage';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { cn } from '@/shared/utils/cn';
import { getPresetDateRange } from '@/shared/utils/get-preset-date-range';
import { requestConfirm } from '@/shared/utils/request-confirm';
import { useSearch } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { Loader } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { askSynclyFilterAtom } from '../../(insight)/chat/atoms/ask-syncly-filter-atom';
import { ChatDisplay } from '../../(insight)/chat/components/chat-display';
import { TemplateQuestionsSection } from '../../(insight)/chat/components/template-questions-section';
import { useDeepResearch } from '../../(insight)/chat/hooks';
import { ChatHeader, useChat } from '../../(insight)/chat/index';
import { ChatInput } from '../ui';

export function AskSynclyPage() {
  const { workspaceId } = useWorkspaceId();
  const { queryId: queryIdParam, startDate, endDate } = useSearch({ strict: false });
  const [queryId, setQueryId] = useState<string | null>(queryIdParam || null);
  const [dateRangeState, setDateRangeState] = useState<DateRangeState>({
    dateRange: {
      startDate: startDate || dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      endDate: endDate || dayjs().format('YYYY-MM-DD'),
    },
  });

  const computedDateRange = useMemo(() => {
    if ('dateRange' in dateRangeState) {
      return dateRangeState.dateRange;
    }
    return getPresetDateRange(dateRangeState.presetName);
  }, [dateRangeState]);

  const askSynclyFilter = useAtomValue(askSynclyFilterAtom);
  const resolvedDateRange = askSynclyFilter?.dateRange ?? computedDateRange;

  const {
    data: chatSession,
    refetch: refetchChatSession,
    isLoading: isLoadingChatSession,
  } = useChat({
    queryId: queryId,
    workspaceId,
    mode: AskMode.GENERAL,
  });

  const {
    data: currentAnswerBlock,
    submit,
    clear: clearDeepResearch,
  } = useDeepResearch({
    sessionId: chatSession?.id,
    queryId: queryId,
    dateRange: resolvedDateRange,
    onDone: async () => {
      await refetchChatSession();
      clearDeepResearch();
    },
  });

  const handleNewSession = async () => {
    if (!queryId) return toast.error('No query selected');
    await requestConfirm({
      title: 'Start a new chat',
      description:
        'Your current conversation will be lost and cannot be recovered. Conversation history will be available soon.',
      confirmButton: {
        children: 'Start New',
      },
      onConfirm: async () => {
        await createNewChatSession(workspaceId, queryId, AskMode.GENERAL);
        await useChat.refetch(workspaceId, queryId, AskMode.GENERAL);
        clearDeepResearch();
      },
    });
  };

  const hasPreviousAnswer = (chatSession?.blocks && chatSession?.blocks?.length > 0) || false;

  return (
    <div className={cn('bg-muted flex h-screen w-full flex-col items-center')}>
      <ChatHeader className="w-full" onNewSession={handleNewSession} />
      <Stage className="flex w-full flex-1 flex-col items-center overflow-y-auto">
        <Stage.Scene status={isLoadingChatSession}>
          <div className="flex max-w-200 flex-1 flex-col justify-center p-6">
            <Loader className="size-4 animate-spin" />
          </div>
        </Stage.Scene>
        <Stage.Scene status={!chatSession?.blocks && !currentAnswerBlock && Boolean(queryId)}>
          <div className="flex max-w-200 flex-1 flex-col justify-center p-6">
            {queryId && (
              <TemplateQuestionsSection
                mode={AskMode.GENERAL}
                onQuestionClick={submit}
                queryId={queryId}
                workspaceId={workspaceId}
              />
            )}
          </div>
        </Stage.Scene>
        <Stage.Scene default>
          <ChatDisplay
            mode={AskMode.GENERAL}
            className="flex max-w-200 flex-1 flex-col"
            session={chatSession}
            currentAnswerBlock={currentAnswerBlock}
            isChatSessionLoading={isLoadingChatSession}
          />
        </Stage.Scene>
      </Stage>

      <div className="w-full max-w-200 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
        <ChatInput
          queryId={queryId}
          onQueryIdChange={setQueryId}
          dateRangeState={dateRangeState}
          onDateRangeStateChange={setDateRangeState}
          className="w-full max-w-200"
          onSubmit={submit}
          loading={
            currentAnswerBlock !== null &&
            currentAnswerBlock?.status !== QuestionAnswerBlockStatus.COMPLETED
          }
          hasPreviousAnswer={hasPreviousAnswer}
        />
      </div>
    </div>
  );
}
